'use client';

import { useEffect } from 'react';
import { useFullscreenNav } from './fullscreen-nav';
import { openDiscover } from './discover/discover-events';

/**
 * Intercept clicks on in-app shareable links (`/u/<name>`, `/p/<id>`,
 * `/content/<id>`, `/c/<id>`) anywhere in the workspace and open them in
 * the FullscreenNav dialog instead of doing a route change that would
 * tear the canvas down. The complaint this fixes: clicking a profile /
 * post / chat link "reloads the page" — really, it navigates away from
 * `/workspace`, unmounting the canvas and any in-flight chats.
 *
 * Implementation: ONE capture-phase click listener on `document`. It
 * runs before Next.js `<Link>`'s own click handler, so a matched link
 * is fully intercepted (`preventDefault` + `stopPropagation`) and never
 * navigates. This catches links from every source — markdown mentions,
 * post cards, agent headers, chat lists — without touching each
 * component.
 *
 * Escape hatches (fall through to normal navigation):
 *   - modifier / middle clicks (open-in-new-tab intent),
 *   - `target="_blank"` or `download` anchors,
 *   - cross-origin hrefs, in-page `#` anchors,
 *   - any path that isn't a known shareable route.
 *
 * Links inside sandboxed widget iframes are separate documents — their
 * clicks never reach this listener — so widgets are unaffected.
 */
export function WorkspaceLinkInterceptor() {
  const { openHref } = useFullscreenNav();

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (e.defaultPrevented) return;
      // Left-click only; let cmd/ctrl/shift/alt + middle-click open a
      // new tab the normal way.
      if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
        return;
      }
      const target = e.target as HTMLElement | null;
      const anchor = target?.closest?.('a[href]') as HTMLAnchorElement | null;
      if (!anchor) return;
      if (anchor.target === '_blank' || anchor.hasAttribute('download')) return;
      const raw = anchor.getAttribute('href');
      if (!raw || raw.startsWith('#')) return;

      let url: URL;
      try {
        url = new URL(raw, window.location.origin);
      } catch {
        return;
      }
      if (url.origin !== window.location.origin) return;

      // Posts and channels open in the DISCOVER overlay at their level —
      // the one surface that owns marketplace browsing post-E1 (full detail
      // view with comments, Back/Escape/history mechanics, and the public
      // /p/ /c/ path stamped on the history entry). Feed cards are already
      // <button>s (via `onOpenPost`) and never reach this listener; this
      // catches post/channel links from chat, mentions, notifications.
      const postMatch = /^\/p\/([^/]+)\/?$/.exec(url.pathname);
      if (postMatch) {
        e.preventDefault();
        e.stopPropagation();
        openDiscover({ kind: 'post', id: decodeURIComponent(postMatch[1]) });
        return;
      }
      const channelMatch = /^\/c\/(.+?)\/?$/.exec(url.pathname);
      if (channelMatch) {
        e.preventDefault();
        e.stopPropagation();
        const slug = decodeURIComponent(channelMatch[1]);
        openDiscover({
          kind: 'channel',
          slug,
          name: slug.split('/').pop() ?? slug,
        });
        return;
      }

      const dialogHref = mapToDialogHref(url.pathname);
      if (!dialogHref) return;

      // Beat Next.js's <Link> handler + the anchor's own onClick.
      e.preventDefault();
      e.stopPropagation();
      openHref(dialogHref);
    };

    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, [openHref]);

  return null;
}

/**
 * Translate a shareable in-app pathname into the FullscreenNav dialog
 * href. Profile / content map to the dedicated preview dialogs (with
 * their "Add to workspace" CTA). Posts are handled separately by the
 * click listener — they drill into the Zap panel in-place rather than
 * opening a dialog. Chat has no preview dialog yet, so it opens via the
 * dialog's same-origin iframe fallback — still no canvas teardown —
 * until a proper chat dialog exists.
 */
function mapToDialogHref(pathname: string): string | null {
  let m: RegExpExecArray | null;
  if ((m = /^\/u\/([^/]+)\/?$/.exec(pathname))) {
    return `/preview/profile?username=${encodeURIComponent(decodeURIComponent(m[1]))}`;
  }
  if ((m = /^\/content\/([^/]+)\/?$/.exec(pathname))) {
    return `/preview/content?contentId=${encodeURIComponent(decodeURIComponent(m[1]))}`;
  }
  return null;
}
